(function (root, factory) {
  var api = factory(root);

  root.PjDesignLabPlacement = api;

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var CURRENT_VERSION = 1;
  var DEFAULT_MAX_PETS = 3;
  var CONFIG_ID = 'pjDesignLabPlacementConfig';
  var REQUIRED_COLORS_ID = 'pjDesignLabPlacementRequiredColors';
  var REQUIRED_STYLES_ID = 'pjDesignLabPlacementRequiredStyles';

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function toNumber(value) {
    var number = Number(value);
    return Number.isFinite(number) ? number : NaN;
  }

  function inPercentRange(value) {
    return Number.isFinite(value) && value >= 0 && value <= 100;
  }

  var HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

  function normalizeHex(value) {
    var hex = String(value == null ? '' : value).trim();
    return HEX_COLOR_PATTERN.test(hex) ? hex.toLowerCase() : '';
  }

  function normalizeColorKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_/]+/g, ' ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Garment configuration from the PDP Style option (Long Sleeve Set, Pants Only, ...).
  var normalizeStyleKey = normalizeColorKey;

  function normalizeMediaId(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw) return '';
    var parts = raw.split('/');
    return parts[parts.length - 1] || raw;
  }

  function parseJson(raw) {
    if (raw == null || raw === '') return null;
    if (isObject(raw) || Array.isArray(raw)) return raw;
    if (typeof raw !== 'string') return null;

    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function parseSafeArea(raw) {
    if (!isObject(raw)) return null;

    var area = {
      x: toNumber(raw.x),
      y: toNumber(raw.y),
      width: toNumber(raw.width),
      height: toNumber(raw.height),
    };

    if (
      !inPercentRange(area.x) ||
      !inPercentRange(area.y) ||
      !inPercentRange(area.width) ||
      !inPercentRange(area.height)
    ) {
      return null;
    }

    return area;
  }

  function parseSlot(raw) {
    if (!isObject(raw)) return null;

    var slot = {
      x: toNumber(raw.x),
      y: toNumber(raw.y),
      width: toNumber(raw.width),
    };

    if (!inPercentRange(slot.x) || !inPercentRange(slot.y) || !inPercentRange(slot.width)) {
      return null;
    }

    if (raw.rotation != null && raw.rotation !== '') {
      var rotation = toNumber(raw.rotation);
      if (!Number.isFinite(rotation)) return null;
      slot.rotation = rotation;
    }

    if (raw.label != null && String(raw.label).trim()) {
      slot.label = String(raw.label).trim();
    }

    var safeArea = parseSafeArea(raw.safeArea);
    if (raw.safeArea != null && !safeArea) return null;
    if (safeArea) slot.safeArea = safeArea;

    return slot;
  }

  function parseSlots(rawSlots, expectedCount) {
    if (!Array.isArray(rawSlots) || rawSlots.length !== expectedCount) return null;

    var slots = [];
    for (var index = 0; index < rawSlots.length; index += 1) {
      var slot = parseSlot(rawSlots[index]);
      if (!slot) return null;
      slots.push(slot);
    }

    return slots;
  }

  function parseTemplate(raw, maxPets) {
    if (!isObject(raw)) return null;

    var color = raw.color != null ? String(raw.color).trim() : '';
    var colorKey = normalizeColorKey(raw.colorKey || color);
    if (!colorKey) return null;

    var style = raw.style != null ? String(raw.style).trim() : '';
    var styleKey = normalizeStyleKey(raw.styleKey || style);
    var mediaId = normalizeMediaId(raw.mediaId || raw.media_id || '');
    var slotsByCount = {};
    var sourceSlots = isObject(raw.slotsByCount) ? raw.slotsByCount : raw.slots;

    for (var count = 1; count <= maxPets; count += 1) {
      var slots = parseSlots(sourceSlots && sourceSlots[String(count)], count);
      if (slots) slotsByCount[String(count)] = slots;
    }

    return {
      color: color || colorKey,
      colorKey: colorKey,
      style: style || styleKey,
      styleKey: styleKey,
      mediaId: mediaId,
      imageUrl: raw.imageUrl ? String(raw.imageUrl).trim() : '',
      backgroundHex: normalizeHex(raw.backgroundHex),
      slotsByCount: slotsByCount,
    };
  }

  function parseCatalog(raw) {
    var parsed = parseJson(raw);
    if (!isObject(parsed)) {
      return { ok: false, reason: 'missing-catalog', version: CURRENT_VERSION, maxPets: DEFAULT_MAX_PETS, templates: [] };
    }

    var version = toNumber(parsed.version);
    if (!Number.isFinite(version) || version < 1) version = CURRENT_VERSION;

    var maxPets = toNumber(parsed.maxPets);
    if (!Number.isFinite(maxPets) || maxPets < 1) maxPets = DEFAULT_MAX_PETS;
    if (maxPets > DEFAULT_MAX_PETS) maxPets = DEFAULT_MAX_PETS;

    var sourceTemplates = Array.isArray(parsed.templates) ? parsed.templates : [];
    var templates = [];
    for (var index = 0; index < sourceTemplates.length; index += 1) {
      var template = parseTemplate(sourceTemplates[index], maxPets);
      if (template) templates.push(template);
    }

    return {
      ok: true,
      version: version,
      maxPets: maxPets,
      productHandle: parsed.productHandle ? String(parsed.productHandle) : '',
      templates: templates,
    };
  }

  // A template with no style covers every style of its color. Once the shopper
  // has picked a style, a template authored for a different one is never used:
  // its print spots sit on the wrong garment (a set's top on a pants-only lay).
  function templatesForStyle(templates, styleKey) {
    var exact = [];
    var styleless = [];

    for (var index = 0; index < templates.length; index += 1) {
      var template = templates[index];
      if (!template.styleKey) styleless.push(template);
      else if (styleKey && template.styleKey === styleKey) exact.push(template);
    }

    if (exact.length) return exact;
    if (styleless.length) return styleless;
    return styleKey ? [] : templates;
  }

  function findTemplate(catalog, query) {
    var colorKey = normalizeColorKey(query && query.color);
    var styleKey = normalizeStyleKey(query && query.style);
    var mediaId = normalizeMediaId(query && query.mediaId);
    var templates = catalog && Array.isArray(catalog.templates) ? catalog.templates : [];
    var candidates = templatesForStyle(
      colorKey
        ? templates.filter(function (template) {
            return template.colorKey === colorKey;
          })
        : templates,
      styleKey
    );

    if (!colorKey) {
      if (mediaId) {
        for (var mediaIndex = 0; mediaIndex < candidates.length; mediaIndex += 1) {
          if (candidates[mediaIndex].mediaId === mediaId) return candidates[mediaIndex];
        }
      }

      // Products split one-per-color have no color axis to read, so a lone
      // template is unambiguous and owns the whole product.
      return !mediaId && candidates.length === 1 ? candidates[0] : null;
    }

    if (candidates.length === 1) return candidates[0];
    if (candidates.length > 1 && mediaId) {
      for (var matchIndex = 0; matchIndex < candidates.length; matchIndex += 1) {
        if (candidates[matchIndex].mediaId === mediaId) return candidates[matchIndex];
      }
    }

    return candidates[0] || null;
  }

  function getSlots(catalog, query) {
    var parsed = catalog && catalog.ok ? catalog : parseCatalog(catalog);
    if (!parsed.ok) {
      return {
        ok: false,
        reason: parsed.reason || 'missing-catalog',
        colorKey: normalizeColorKey(query && query.color),
        petCount: Number(query && query.petCount) || 0,
        slots: [],
      };
    }

    var petCount = Math.floor(toNumber(query && query.petCount));
    if (!Number.isFinite(petCount) || petCount < 1 || petCount > parsed.maxPets) {
      return {
        ok: false,
        reason: 'unsupported-pet-count',
        colorKey: normalizeColorKey(query && query.color),
        petCount: petCount,
        slots: [],
      };
    }

    var template = findTemplate(parsed, query || {});
    if (!template) {
      return {
        ok: false,
        reason: 'missing-template',
        colorKey: normalizeColorKey(query && query.color),
        styleKey: normalizeStyleKey(query && query.style),
        petCount: petCount,
        slots: [],
      };
    }

    var slots = template.slotsByCount[String(petCount)];
    if (!slots) {
      return {
        ok: false,
        reason: 'missing-slots',
        colorKey: template.colorKey,
        styleKey: template.styleKey,
        mediaId: template.mediaId,
        petCount: petCount,
        slots: [],
      };
    }

    return {
      ok: true,
      reason: '',
      version: parsed.version,
      color: template.color,
      colorKey: template.colorKey,
      style: template.style,
      styleKey: template.styleKey,
      mediaId: template.mediaId,
      imageUrl: template.imageUrl,
      backgroundHex: template.backgroundHex,
      petCount: petCount,
      slots: slots,
    };
  }

  // Safe layout used when the catalog has no usable template, so a missing or
  // broken metafield degrades to a centered placement instead of no preview.
  var DEFAULT_SLOTS = {
    1: [{ x: 50, y: 50, width: 40 }],
    2: [{ x: 32, y: 50, width: 30 }, { x: 68, y: 50, width: 30 }],
    3: [{ x: 22, y: 50, width: 24 }, { x: 50, y: 50, width: 24 }, { x: 78, y: 50, width: 24 }],
  };

  function getSlotsOrDefault(catalog, query) {
    var result = getSlots(catalog, query);
    var recoverable = ['missing-catalog', 'missing-template', 'missing-slots'];
    var slots = DEFAULT_SLOTS[result.petCount];
    if (result.ok || recoverable.indexOf(result.reason) < 0 || !slots) return result;

    return {
      ok: true,
      fallback: true,
      reason: result.reason,
      color: String((query && query.color) || '').trim(),
      colorKey: result.colorKey,
      mediaId: normalizeMediaId(query && query.mediaId),
      imageUrl: '',
      petCount: result.petCount,
      slots: slots,
    };
  }

  // Without required styles each entry is a color, as before. With them, every
  // purchasable color × style combination is checked and reported as a pair.
  function checkCoverage(catalog, requiredColors, requiredStyles) {
    var parsed = catalog && catalog.ok ? catalog : parseCatalog(catalog);
    var colors = Array.isArray(requiredColors) ? requiredColors : [];
    var styles = Array.isArray(requiredStyles) && requiredStyles.length ? requiredStyles : [''];
    var missing = [];
    var covered = [];

    function entry(color, style) {
      return style ? { color: color, style: style } : { color: color };
    }

    if (!parsed.ok) {
      colors.forEach(function (color) {
        styles.forEach(function (style) {
          missing.push(Object.assign(entry(color, style), { reason: 'missing-catalog' }));
        });
      });
      return { complete: missing.length === 0, missing: missing, covered: [] };
    }

    for (var index = 0; index < colors.length; index += 1) {
      for (var styleIndex = 0; styleIndex < styles.length; styleIndex += 1) {
        var color = colors[index];
        var style = styles[styleIndex];
        var reason = '';

        for (var petCount = 1; petCount <= parsed.maxPets; petCount += 1) {
          var result = getSlots(parsed, { color: color, style: style, petCount: petCount });
          if (!result.ok) {
            reason = result.reason;
            break;
          }
        }

        if (reason) missing.push(Object.assign(entry(color, style), { reason: reason }));
        else covered.push(style ? entry(color, style) : color);
      }
    }

    return {
      complete: missing.length === 0,
      missing: missing,
      covered: covered,
    };
  }

  function readScriptJson(doc, id) {
    if (!doc || typeof doc.getElementById !== 'function') return null;
    var node = doc.getElementById(id);
    if (!node) return null;
    return parseJson(node.textContent);
  }

  function loadFromDocument(doc) {
    var catalog = parseCatalog(readScriptJson(doc, CONFIG_ID));
    var requiredColors = readScriptJson(doc, REQUIRED_COLORS_ID);
    var requiredStyles = readScriptJson(doc, REQUIRED_STYLES_ID);
    var coverage = checkCoverage(
      catalog,
      Array.isArray(requiredColors) ? requiredColors : [],
      Array.isArray(requiredStyles) ? requiredStyles : []
    );

    return {
      catalog: catalog,
      coverage: coverage,
    };
  }

  // QA/support entry point: explains why a color resolves or does not, without
  // needing a debugger. Call PjDesignLabPlacement.debug() in the console.
  function diagnose(doc) {
    var target = doc || root.document;
    var node =
      target && typeof target.getElementById === 'function'
        ? target.getElementById(CONFIG_ID)
        : null;
    var raw = node ? String(node.textContent || '').trim() : '';
    var parsedRaw = parseJson(raw);
    var catalog = parseCatalog(parsedRaw);
    var requiredColors = readScriptJson(target, REQUIRED_COLORS_ID);
    var colors = Array.isArray(requiredColors) ? requiredColors : [];
    var requiredStyles = readScriptJson(target, REQUIRED_STYLES_ID);
    var styles = Array.isArray(requiredStyles) ? requiredStyles : [];

    var rawType = 'empty-or-invalid-json';
    if (typeof parsedRaw === 'string') {
      rawType =
        'string — double-encoded, so the metafield is probably Single line text rather than JSON; tolerated but worth fixing';
    } else if (Array.isArray(parsedRaw)) {
      rawType = 'array — expected an object with a templates array';
    } else if (isObject(parsedRaw)) {
      rawType = 'object';
    }

    return {
      configScriptFound: Boolean(node),
      metafieldPresent: raw !== '' && raw !== 'null',
      metafieldValueType: rawType,
      rawLength: raw.length,
      rawPreview: raw.slice(0, 300),
      parsedOk: catalog.ok,
      parseReason: catalog.reason || '',
      version: catalog.version,
      maxPets: catalog.maxPets,
      templates: (catalog.templates || []).map(function (template) {
        return {
          color: template.color,
          colorKey: template.colorKey,
          style: template.style,
          styleKey: template.styleKey,
          mediaId: template.mediaId,
          petCountsConfigured: Object.keys(template.slotsByCount),
        };
      }),
      productColorOptions: colors,
      productStyleOptions: styles,
      coverage: checkCoverage(catalog, colors, styles),
    };
  }

  function debug(doc) {
    var report = diagnose(doc);
    if (root.console && typeof root.console.log === 'function') {
      root.console.log('[PJ Design Lab] placement diagnostics', report);
    }
    return report;
  }

  var loaded = null;

  function load() {
    loaded = loadFromDocument(root.document);
    return loaded;
  }

  function current() {
    return loaded || load();
  }

  if (root.document) load();

  return {
    CURRENT_VERSION: CURRENT_VERSION,
    DEFAULT_MAX_PETS: DEFAULT_MAX_PETS,
    CONFIG_ID: CONFIG_ID,
    REQUIRED_COLORS_ID: REQUIRED_COLORS_ID,
    REQUIRED_STYLES_ID: REQUIRED_STYLES_ID,
    normalizeColorKey: normalizeColorKey,
    normalizeStyleKey: normalizeStyleKey,
    normalizeMediaId: normalizeMediaId,
    parseCatalog: parseCatalog,
    getSlots: getSlots,
    getSlotsOrDefault: getSlotsOrDefault,
    checkCoverage: checkCoverage,
    loadFromDocument: loadFromDocument,
    diagnose: diagnose,
    debug: debug,
    load: load,
    current: current,
  };
});
